import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useCreateInvoice, useCreateCustomer, useListCustomers, useListInvoices, getListCustomersQueryKey, useAttachInvoice, getProjectAggregatesQueryKey, getProjectInvoicesQueryKey, getProjectDocumentLinksQueryKey } from "@workspace/api-client-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2, Check, Hash, PlusCircle, RefreshCw, Info } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CreateCustomerModal, type CreateCustomerFormValues } from "@/components/create-customer-modal";
import { ProGate } from "@/components/pro-gate";
import { EditorDisclaimer } from "@/components/editor-disclaimer";
import type { ProFeatureKey } from "@workspace/pricing";
import { trackEvent } from "@/lib/analytics";

const GATE_RECURRING: ProFeatureKey = "RECURRING_INVOICES";

const RECURRING_INTERVAL_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannually", label: "Semi-annually" },
  { value: "annually", label: "Annually" },
  { value: "custom", label: "Custom" },
] as const;

const invoiceSchema = z.object({
  customerId: z.number({ required_error: "Please select a customer" }).min(1, "Please select a customer"),
  invoiceNumber: z.string().optional(),
  invoiceDate: z.string().min(1, "Date is required"),
  dueDate: z.string().optional(),
  description: z.string().optional(),
});

function nextInvoiceNumberPreview(lastNumber: string | null | undefined): string {
  if (!lastNumber) return "INV-0001";
  if (lastNumber.startsWith("INV-")) {
    const num = parseInt(lastNumber.slice(4), 10);
    if (!isNaN(num)) return `INV-${String(num + 1).padStart(4, "0")}`;
  }
  return "INV-0001";
}

export default function NewInvoice() {
  usePageTitle("New Invoice");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams(window.location.search);
  const initialCustomerId = queryParams.get("customer") ? parseInt(queryParams.get("customer")!, 10) : undefined;
  const initialProjectId = queryParams.get("project") ? parseInt(queryParams.get("project")!, 10) : undefined;
  const attachInvoice = useAttachInvoice();

  const { data: customers, isLoading: isLoadingCustomers } = useListCustomers();
  const { data: allInvoices } = useListInvoices({});
  const createInvoice = useCreateInvoice();
  const createCustomer = useCreateCustomer();

  const [customerSearch, setCustomerSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState("");

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState("monthly");
  const [recurringStartDate, setRecurringStartDate] = useState("");
  const [recurringEndDate, setRecurringEndDate] = useState("");

  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysOut = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const lastInvoiceNumber = allInvoices && allInvoices.length > 0
    ? allInvoices.reduce((latest, inv) => {
        if (!latest) return inv;
        return new Date(inv.createdAt).getTime() > new Date(latest.createdAt).getTime() ? inv : latest;
      }, null as typeof allInvoices[0] | null)?.invoiceNumber ?? null
    : null;

  const nextNumber = nextInvoiceNumberPreview(lastInvoiceNumber);

  const form = useForm<z.infer<typeof invoiceSchema>>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoiceNumber: "",
      invoiceDate: today,
      dueDate: thirtyDaysOut,
      description: "",
      customerId: initialCustomerId || undefined,
    },
  });

  useEffect(() => {
    if (allInvoices !== undefined) {
      const current = form.getValues("invoiceNumber");
      if (!current) form.setValue("invoiceNumber", nextNumber);
    }
  }, [allInvoices]);

  const onSubmit = (data: z.infer<typeof invoiceSchema>) => {
    createInvoice.mutate({
      data: {
        invoiceNumber: data.invoiceNumber || undefined,
        customerId: data.customerId,
        invoiceDate: data.invoiceDate,
        dueDate: data.dueDate || null,
        description: data.description,
        isRecurring,
        ...(isRecurring ? {
          recurringInterval,
          recurringStartDate: recurringStartDate || null,
          recurringEndDate: recurringEndDate || null,
        } : {}),
      }
    }, {
      onSuccess: (invoice) => {
        toast({ title: "Invoice created successfully" });
        // Server-backed per-user dedup via userEventsTable; falls back silently on error
        fetch("/api/user/activate", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } })
          .then(r => r.json())
          .then(({ firstTime }: { firstTime: boolean }) => { if (firstTime) trackEvent("Activated"); })
          .catch(() => {});
        if (initialProjectId) {
          attachInvoice.mutate(
            { projectId: initialProjectId, invoiceId: invoice.id },
            {
              onSettled: () => {
                queryClient.invalidateQueries({ queryKey: getProjectInvoicesQueryKey(initialProjectId) });
                queryClient.invalidateQueries({ queryKey: getProjectAggregatesQueryKey(initialProjectId) });
                queryClient.invalidateQueries({ queryKey: getProjectDocumentLinksQueryKey() });
                setLocation(`/invoices/${invoice.id}`);
              },
            },
          );
          return;
        }
        setLocation(`/invoices/${invoice.id}`);
      },
      onError: () => {
        toast({ title: "Failed to create invoice", variant: "destructive" });
      }
    });
  };

  const handleCreateCustomer = (data: CreateCustomerFormValues) => {
    createCustomer.mutate(
      {
        data: {
          name: data.name,
          email: data.email || undefined,
          phone: data.phone || undefined,
          addressLine1: data.addressLine1 || undefined,
        },
      },
      {
        onSuccess: (newCustomer) => {
          queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
          form.setValue("customerId", newCustomer.id);
          setCustomerSearch("");
          setCreateModalOpen(false);
        },
        onError: () => {
          toast({ title: "Failed to create customer", variant: "destructive" });
        },
      }
    );
  };

  const intervalLabel = RECURRING_INTERVAL_OPTIONS.find(o => o.value === recurringInterval)?.label ?? "Monthly";

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in duration-300">
      <Link href="/invoices" className="text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-1 w-fit transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to Invoices
      </Link>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create New Invoice</h1>
        <p className="text-muted-foreground mt-1">Create an invoice for a customer.</p>
      </div>

      <Card className="shadow-sm border-border/50">
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="customerId"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Customer</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn("w-full justify-between font-normal", !field.value && "text-muted-foreground")}
                            disabled={isLoadingCustomers}
                          >
                            {field.value ? customers?.find((c) => c.id === field.value)?.name : "Select customer..."}
                            <span className="opacity-50 shrink-0">▼</span>
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput
                            placeholder="Search customer..."
                            value={customerSearch}
                            onValueChange={setCustomerSearch}
                          />
                          <CommandList>
                            {(() => {
                              const trimmed = customerSearch.trim();
                              const hasExactMatch = customers?.some(c => c.name.toLowerCase() === trimmed.toLowerCase());
                              const showCreate = trimmed.length > 0 && !hasExactMatch;
                              return (
                                <>
                                  {!customers?.length && !showCreate && <CommandEmpty>No customer found.</CommandEmpty>}
                                  {customers && customers.length > 0 && (
                                    <CommandGroup>
                                      {customers.map((customer) => (
                                        <CommandItem
                                          key={customer.id}
                                          value={customer.name}
                                          onSelect={() => {
                                            form.setValue("customerId", customer.id);
                                            setCustomerSearch("");
                                            document.body.click();
                                          }}
                                        >
                                          <Check className={cn("mr-2 h-4 w-4", customer.id === field.value ? "opacity-100" : "opacity-0")} />
                                          {customer.name}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  )}
                                  {showCreate && (
                                    <CommandGroup>
                                      <CommandItem
                                        value={`__create__${trimmed}`}
                                        onSelect={() => {
                                          setPendingCustomerName(trimmed);
                                          setCreateModalOpen(true);
                                          document.body.click();
                                        }}
                                      >
                                        <PlusCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                                        Create &quot;{trimmed}&quot;
                                      </CommandItem>
                                    </CommandGroup>
                                  )}
                                </>
                              );
                            })()}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="invoiceNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                        Invoice #
                      </FormLabel>
                      <FormControl>
                        <Input {...field} className="font-mono" placeholder={nextNumber} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="invoiceDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dueDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description of the work being invoiced..."
                        className="min-h-[100px] resize-y"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ProGate feature={GATE_RECURRING} mode="overlay">
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Recurring invoice</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Learn about recurring invoices">
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 text-sm" align="start">
                        <p className="font-medium mb-1">How recurring invoices work</p>
                        <p className="text-muted-foreground">
                          When recurring is enabled, this invoice acts as a <strong>parent template</strong>. QuoTrak automatically generates a new draft copy on each billing date — you review and send each one at your convenience.
                        </p>
                      </PopoverContent>
                    </Popover>
                    {isRecurring && (
                      <span className="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 px-2 py-0.5 rounded-full font-medium">
                        {intervalLabel}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isRecurring}
                    onClick={() => setIsRecurring(v => !v)}
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                      isRecurring ? "bg-primary" : "bg-muted"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                        isRecurring ? "translate-x-6" : "translate-x-1"
                      )}
                    />
                  </button>
                </div>

                {isRecurring && (
                  <div className="space-y-3 pt-1">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1.5 block">Billing cycle</label>
                      <Select value={recurringInterval} onValueChange={setRecurringInterval}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRING_INTERVAL_OPTIONS.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Start date (optional)</label>
                        <Input
                          type="date"
                          value={recurringStartDate}
                          onChange={e => setRecurringStartDate(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">End date (optional)</label>
                        <Input
                          type="date"
                          value={recurringEndDate}
                          onChange={e => setRecurringEndDate(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      A new draft copy will be generated automatically on each billing date.
                    </p>
                  </div>
                )}
              </div>
              </ProGate>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setLocation("/invoices")}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createInvoice.isPending}>
                  {createInvoice.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Invoice
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <CreateCustomerModal
        open={createModalOpen}
        initialName={pendingCustomerName}
        isSubmitting={createCustomer.isPending}
        onOpenChange={setCreateModalOpen}
        onSubmit={handleCreateCustomer}
      />

      <EditorDisclaimer />
    </div>
  );
}
